// M.E.T.I.S settings editor: nail timer + D.E.S.I.G.N templates. Mirrors
// nail_timer_seconds / metis_design_templates columns (see
// src/interfaces/settings.ts MetisDesignTemplate, settings-context.tsx).
import React, { useState } from 'react';
import { Alert, Pressable, StyleSheet, Text, View } from 'react-native';
import { ThemedSwitch } from './ThemedSwitch';
import { makeThemedStyles } from '../../v10/theme';
import { SectionTitle, Field, GoldButton, GlassCard } from '../../v10/ui';
import { useSettingColumn } from './useSettingColumn';

interface MetisDesignRow {
  label: string;
  value: string;
}

interface MetisDesignTemplate {
  id: string;
  name: string;
  rows: MetisDesignRow[];
}

const DEFAULT_ROWS: MetisDesignRow[] = [
  { label: '', value: '' },
  { label: '', value: '' },
  { label: '', value: '' },
];

function makeTemplateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

export default function MetisEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { value: nailTimerSeconds, setValue: setNailTimerSeconds } = useSettingColumn<number>(
    userId,
    'nail_timer_seconds',
    300
  );
  const { value: templates, setValue: setTemplates } = useSettingColumn<MetisDesignTemplate[]>(
    userId,
    'metis_design_templates',
    []
  );
  const { value: profilesEnabled, setValue: setProfilesEnabled } = useSettingColumn<boolean>(
    userId,
    'metis_profiles_enabled',
    false
  );
  const { value: missReasons, setValue: setMissReasons } = useSettingColumn<string[]>(
    userId,
    'metis_miss_reasons',
    []
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newReason, setNewReason] = useState('');

  const addReason = () => {
    const trimmed = newReason.trim();
    if (!trimmed || missReasons.includes(trimmed)) return;
    setMissReasons([...missReasons, trimmed]);
    setNewReason('');
  };
  const updateReason = (idx: number, text: string) =>
    setMissReasons(missReasons.map((r, i) => (i === idx ? text : r)));
  const deleteReason = (idx: number) => setMissReasons(missReasons.filter((_, i) => i !== idx));

  const handleMinutesChange = (text: string) => {
    const parsedMinutes = parseInt(text, 10);
    const minutes = Number.isNaN(parsedMinutes) ? 1 : parsedMinutes;
    const seconds = Math.max(60, minutes * 60);
    setNailTimerSeconds(seconds);
  };

  const addTemplate = () => {
    const next: MetisDesignTemplate = {
      id: makeTemplateId(),
      name: 'New Template',
      rows: DEFAULT_ROWS.map((r) => ({ ...r })),
    };
    setTemplates([...templates, next]);
    setEditingId(next.id);
  };

  const updateTemplate = (id: string, patch: Partial<MetisDesignTemplate>) => {
    setTemplates(templates.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  const updateRow = (id: string, rowIdx: number, patch: Partial<MetisDesignRow>) => {
    setTemplates(
      templates.map((t) =>
        t.id === id ? { ...t, rows: t.rows.map((r, i) => (i === rowIdx ? { ...r, ...patch } : r)) } : t
      )
    );
  };

  const deleteTemplate = (id: string) => {
    Alert.alert('Delete template?', 'This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => {
          setTemplates(templates.filter((t) => t.id !== id));
          if (editingId === id) setEditingId(null);
        },
      },
    ]);
  };

  const editingTemplate = templates.find((t) => t.id === editingId) ?? null;

  return (
    <View>
      <GlassCard style={styles.card}>
        <SectionTitle style={styles.sectionGap}>Profiles</SectionTitle>
        <View style={styles.row}>
          <Text style={styles.label}>Enable profiles</Text>
          <ThemedSwitch
            value={profilesEnabled}
            onValueChange={setProfilesEnabled}
          />
        </View>
        <Text style={styles.hint}>
          When off, METIS shows a single daily agenda without time-segment profiles.
        </Text>
      </GlassCard>

      <GlassCard style={styles.card}>
        <SectionTitle style={styles.sectionGap}>Miss Reasons</SectionTitle>
        <Text style={styles.hint}>
          Options shown when a task is marked “Missed” in the agenda. Pick from this list to record why.
        </Text>
        {missReasons.length === 0 ? (
          <Text style={styles.hint}>No reasons yet — add one below.</Text>
        ) : (
          missReasons.map((r, idx) => (
            <View key={idx} style={styles.rowEditor}>
              <Field style={styles.flex1}
                inputStyle={[styles.input, { flex: 1, marginTop: 0 }]}
                value={r}
                onChangeText={(text) => updateReason(idx, text)}
                placeholder="Reason"
                  />
              <Pressable onPress={() => deleteReason(idx)} style={styles.smallDangerButton}>
                <Text style={styles.smallDangerText}>Delete</Text>
              </Pressable>
            </View>
          ))
        )}
        <View style={styles.rowEditor}>
          <Field style={styles.flex1}
            inputStyle={[styles.input, { flex: 1, marginTop: 0 }]}
            value={newReason}
            onChangeText={setNewReason}
            placeholder="Add a reason…"
            onSubmitEditing={addReason}
            returnKeyType="done"
          />
          <Pressable onPress={addReason} style={styles.smallButton}>
            <Text style={styles.smallButtonText}>Add</Text>
          </Pressable>
        </View>
      </GlassCard>

      <GlassCard style={styles.card}>
        <SectionTitle style={styles.sectionGap}>Nail Timer</SectionTitle>
        <View style={styles.row}>
          <Text style={styles.label}>Minutes</Text>
          <Field
            inputStyle={styles.numberInput}
            keyboardType="number-pad"
            value={String(Math.floor(nailTimerSeconds / 60))}
            onChangeText={handleMinutesChange}
          />
        </View>
      </GlassCard>

      <GlassCard style={styles.card}>
        <SectionTitle style={styles.sectionGap}>ORDER Templates</SectionTitle>

        {editingTemplate ? (
          <View>
            <Text style={styles.label}>Name</Text>
            <Field
              inputStyle={styles.input}
              value={editingTemplate.name}
              onChangeText={(text) => updateTemplate(editingTemplate.id, { name: text })}
            />

            {editingTemplate.rows.map((row, idx) => (
              <View key={idx} style={styles.rowEditor}>
                <Field
                  inputStyle={[styles.input, styles.rowLabelInput]}
                  placeholder="Label"
                  value={row.label}
                  onChangeText={(text) => updateRow(editingTemplate.id, idx, { label: text })}
                />
                <Field
                  inputStyle={[styles.input, styles.rowValueInput]}
                  placeholder="Value"
                  value={row.value}
                  onChangeText={(text) => updateRow(editingTemplate.id, idx, { value: text })}
                />
              </View>
            ))}

            <GoldButton ghost label="Done" onPress={() => setEditingId(null)} style={styles.actionGap} />
          </View>
        ) : (
          <View>
            {templates.length === 0 ? (
              <Text style={styles.hint}>No templates yet.</Text>
            ) : (
              templates.map((t) => (
                <View key={t.id} style={styles.templateRow}>
                  <Text style={styles.templateName}>{t.name}</Text>
                  <View style={styles.templateActions}>
                    <Pressable onPress={() => setEditingId(t.id)} style={styles.smallButton}>
                      <Text style={styles.smallButtonText}>Edit</Text>
                    </Pressable>
                    <Pressable onPress={() => deleteTemplate(t.id)} style={styles.smallDangerButton}>
                      <Text style={styles.smallDangerText}>Delete</Text>
                    </Pressable>
                  </View>
                </View>
              ))
            )}
            <GoldButton label="Add Template" onPress={addTemplate} style={styles.actionGap} />
          </View>
        )}
      </GlassCard>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  flex1: { flex: 1 },
  actionGap: { marginTop: 8 },
  sectionGap: { marginTop: 18 },
  card: {
    padding: 14,
    marginTop: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  label: { fontSize: 14, fontWeight: '700', color: t.ivory },
  hint: { fontSize: 12, color: t.ivorySoft, fontStyle: 'italic', marginBottom: 8 },
  numberInput: {
    minWidth: 60,
    textAlign: 'center',
  },
  input: {
    marginTop: 6,
  },
  rowEditor: { flexDirection: 'row', gap: 8, marginTop: 8 },
  rowLabelInput: { flex: 1, marginTop: 0 },
  rowValueInput: { flex: 2, marginTop: 0 },
  templateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  templateName: { fontSize: 14, fontWeight: '700', color: t.ivory, flex: 1 },
  templateActions: { flexDirection: 'row', gap: 8 },
  smallButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  smallButtonText: { fontSize: 12, fontWeight: '700', color: t.goldBright },
  smallDangerButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  smallDangerText: { fontSize: 12, fontWeight: '700', color: t.lapsed },
}),
);
